import { createMRTColumnHelper } from "material-react-table";
import { useTranslation } from "react-i18next";
import { FoodsIcon, Typography } from "@foodpilot/foods";
import { Link } from "react-router";
import { Box, useTheme } from "@mui/material";
import { URLHandler } from "src/services/URLHandler";
import { ListGrid } from "src/pages/Product/ProductForm/ListGrid";
import {
    getPackagingKind,
    getPackagingKindIcon,
    PackagingGridEntity,
    PackagingGridProps,
    PackagingKind,
    usePackagingListGridCore,
} from "./packagingListGridCore";

export function PackagingListGrid(props: PackagingGridProps) {
    const { setNewPackagings, existingPackagings, delayedSearch } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const {
        canManagePackagings,
        isLoading,
        newPackagingHandler,
        packagings,
        pagination,
        selectedCompany,
        selectionHandler,
        setPagination,
        setSorting,
        sorting,
        totalItems,
    } = usePackagingListGridCore(delayedSearch);

    if (selectedCompany === null) return null;

    const columnHelper = createMRTColumnHelper<PackagingGridEntity>();
    const noValue = "-";
    const getPackagingKindLabel = (kind: PackagingKind) =>
        kind === "element" ?
            t("packaging.create_choice.component.title", "Packaging component")
        :   t("packaging.create_choice.unit.title", "Packaging unit");
    const getExpectedValueLabel = (kind: PackagingKind) =>
        kind === "element" ? t("packaging_element.weight", "Weight (g)") : t("packaging_composite.amount", "Quantity");
    const columns = [
        columnHelper.accessor("name", {
            header: t("packagings.name"),
            Cell: (value) => {
                const kind = getPackagingKind(value.row.original);
                const typeIndicator =
                    kind === null ? null : (
                        <Box
                            component="span"
                            aria-label={getPackagingKindLabel(kind)}
                            title={getPackagingKindLabel(kind)}
                            sx={{
                                alignItems: "center",
                                color: "text.secondary",
                                display: "inline-flex",
                            }}
                        >
                            <FoodsIcon icon={getPackagingKindIcon(kind)} size={1} />
                        </Box>
                    );

                if (!canManagePackagings) {
                    return (
                        <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                            {typeIndicator}
                            <Typography variant="body" sx={{ color: "text.primary" }}>
                                {value.renderedCellValue}
                            </Typography>
                        </Box>
                    );
                }

                const companyId = selectedCompany.id;
                const packagingId = value.row.original.id;

                return (
                    <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                        {typeIndicator}
                        <Link
                            to={URLHandler.packaging.scores(companyId, packagingId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: theme.palette.primary[700],
                                textDecoration: "none",
                                display: "flex",
                                justifyContent: "left",
                                gap: theme.spacing(0.625),
                                alignItems: "center",
                            }}
                        >
                            {value.renderedCellValue}
                            <FoodsIcon icon="openNewTab" size={2} />
                        </Link>
                    </Box>
                );
            },
        }),
        columnHelper.accessor(
            (row) => {
                const kind = getPackagingKind(row);
                return kind === null ? noValue : getExpectedValueLabel(kind);
            },
            {
                id: "expectedValue",
                header: t("packagings.form.quantity", "Quantity / Weight (g)"),
                Cell: (value) => (
                    <Typography variant="body" sx={{ color: "text.primary" }}>
                        {value.renderedCellValue}
                    </Typography>
                ),
            },
        ),
        columnHelper.accessor((row) => ("kind" in row ? row.code : row.externalId) ?? noValue, {
            id: "externalId",
            header: t("unique_identifier"),
            Cell: (value) => (
                <Typography variant="body" sx={{ color: "text.primary" }}>
                    {value.renderedCellValue}
                </Typography>
            ),
        }),
    ];

    return (
        <ListGrid
            data={packagings}
            columns={columns}
            delayedSearch={delayedSearch}
            existingEntities={existingPackagings}
            setNewEntities={setNewPackagings}
            selectionHandler={selectionHandler}
            newEntityHandler={newPackagingHandler}
            isLoading={isLoading}
            totalItems={totalItems}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
        />
    );
}
